import {
  MeetingCard,
  WeekColumn,
  WeekColumnHead,
  WeekEmpty,
  WeekGrid,
  useNow,
} from '@wundo/ui';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { isSameDay } from '@/lib/week.ts';
import {
  formatDayOfMonth,
  formatTimeAndDuration,
  formatWeekdayShort,
  meetingTitle,
} from './meetingFormat.ts';
import {
  followedJourneySuggestion,
  meetingCardLabel,
  meetingState,
} from './meetingCardState.ts';
import { AgendaMeetingPill } from './AgendaMeetingPill.tsx';
import { AgendaJourneyLinkPill } from './AgendaJourneyLinkPill.tsx';

export interface AgendaWeekViewProps {
  days: Date[];
  meetings: CalendarMeetingItem[];
  /* The click: open the conversation, or add the meeting to Wundo. */
  onSelect: (meeting: CalendarMeetingItem) => void;
  /* The suggested journey step's own pill, on a card whose click opens the meeting. */
  onLink: (meeting: CalendarMeetingItem) => void;
}

export function AgendaWeekView({
  days,
  meetings,
  onSelect,
  onLink,
}: AgendaWeekViewProps) {
  const today = useNow();

  return (
    <WeekGrid columns={days.length}>
      {days.map((day) => {
        const dayMeetings = meetings.filter((meeting) =>
          isSameDay(new Date(meeting.starts_at), day),
        );

        return (
          <div key={day.toISOString()}>
            <WeekColumnHead
              day={formatWeekdayShort(day)}
              date={formatDayOfMonth(day)}
              today={isSameDay(day, today)}
            />
            <WeekColumn>
              {dayMeetings.map((meeting) => {
                const state = meetingState(meeting);
                const suggestion = followedJourneySuggestion(meeting);

                return (
                  <MeetingCard
                    key={meeting.id}
                    title={meetingTitle(meeting)}
                    time={formatTimeAndDuration(meeting)}
                    state={state}
                    aria-label={meetingCardLabel(meeting)}
                    onClick={() => onSelect(meeting)}
                    badge={
                      state !== 'inert' && (
                        <AgendaMeetingPill meeting={meeting} state={state} />
                      )
                    }
                    action={
                      suggestion && (
                        <AgendaJourneyLinkPill
                          suggestion={suggestion}
                          onLink={() => onLink(meeting)}
                        />
                      )
                    }
                  />
                );
              })}
              {dayMeetings.length === 0 && <WeekEmpty>—</WeekEmpty>}
            </WeekColumn>
          </div>
        );
      })}
    </WeekGrid>
  );
}
